<script lang="ts" setup>
import type { AxiosError } from "axios";
import type { RoleDto } from "mws-identity-ts-sdk";
import { ref, type Ref } from "vue";
import { useRouter } from "vue-router";
import { useToast } from "vue-toastification";

import PageWrapper from "@/common/components/PageWrapper.vue";
import {
  type FormKitErrors,
  getFormKitErrors,
} from "@/common/utils/getErrorMessage";
import {
  useCreateRoleMutation,
  useGetPermissionsQuery,
} from "@/roles/api/queries";
import RoleDetailsComponent from "@/roles/components/RoleDetailsComponent.vue";

import type { Permission } from "../types";

definePage({
  meta: {
    auth: ["Developer.Access"],
  },
});

const toast = useToast();
const errors: Ref<FormKitErrors | undefined> = ref(undefined);
const router = useRouter();

const onError = (err: AxiosError) => {
  toast.error("Something went wrong creating the role");
  errors.value = getFormKitErrors(err);
};

const onCreate = async (result: RoleDto) => {
  toast.success("Successfully updated role");

  await router.push(`/roles/${result.id}`);
};

const { mutateAsync } = useCreateRoleMutation(onError, onCreate);
const { data: permissions, isPending: isPending } = useGetPermissionsQuery();
</script>

<template>
  <PageWrapper
    :is-pending="isPending"
    title="Create role"
    :breadcrumb-links="[
      { label: 'All roles', to: '/roles' },
      { label: 'Create role', to: '#' },
    ]"
  >
    <RoleDetailsComponent
      :errors="errors"
      create-or-update="CREATE"
      :current-role="{ permissions: [], name: '', description: '', id: '' }"
      :permissions="permissions as Permission[]"
      data-test="create-role-form"
      @submit-clicked="mutateAsync"
    />
  </PageWrapper>
</template>
